import type { AnyAction } from '@reduxjs/toolkit';
import type { FeesAndDiscounts } from '@buy-viasat/types/build/bv';
import { cartActions } from '../cart';
import { appActions } from '../app';
import { paymentInformationActions } from '../payment-information';
import { personalInformationActions } from '../personal-information';
import { creditVerificationActions } from './slice';

/**
 * Actions to dispatch when a credit check is skipped because the inputs match
 * the last successful check's signature. Shared by the saga (`yield put`) and
 * the useCreditVerification hook (`dispatch`) to avoid duplicating the block.
 */
export function buildSkippedCreditCheckActions(): AnyAction[] {
  return [
    creditVerificationActions.setIsLoading(false),
    creditVerificationActions.updateCreditVerification(false),
    personalInformationActions.setIsPersonalInformationLoading(false),
    // Restore the eligible path's "success" display state so the cart doesn't stay stuck on "Calculated Later".
    creditVerificationActions.setCreditCheckComplete(true),
    appActions.setDisplayCalculatedLaterCartLabel(false),
  ];
}

type EligibleCreditCheckInput = {
  totalWithoutPromos: number | null | undefined;
  feesAndDiscounts: FeesAndDiscounts[] | null | undefined;
  onetimeDiscount: number;
  useNewApi?: boolean;
};

/**
 * Builds the ordered list of Redux actions to dispatch after a successful
 * (eligible) credit check. Returning plain actions lets the same logic be
 * consumed by both redux-saga (via `yield put`) and React hooks (via
 * `dispatch`) without duplicating business logic.
 */
export function buildEligibleCreditCheckActions({
  totalWithoutPromos,
  feesAndDiscounts,
  onetimeDiscount,
  useNewApi,
}: EligibleCreditCheckInput): AnyAction[] {
  const actions: AnyAction[] = [];

  if (totalWithoutPromos !== null && totalWithoutPromos !== undefined) {
    // we want to override the default due today values with the new Credit Check ones
    // even if the new values are 0$ (Free)
    actions.push(cartActions.setOnetimeTotalWithPromo(totalWithoutPromos - onetimeDiscount));
    actions.push(cartActions.setOnetimeTotalWithoutPromo(totalWithoutPromos));
  }

  if (Array.isArray(feesAndDiscounts) && feesAndDiscounts.length > 0) {
    actions.push(cartActions.setFeesAndDiscounts(feesAndDiscounts));
  }

  actions.push(creditVerificationActions.setCreditCheckComplete(true));
  actions.push(appActions.setDisplayCalculatedLaterCartLabel(false));
  if (!useNewApi) {
    actions.push(paymentInformationActions.getTaxCodes());
  }
  actions.push(appActions.setIsPersonalInformationComplete(true));

  return actions;
}
